import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import { SCIFI, armor, glow, glowColor, bake, beam, prism } from './units-style.js';

// Gunship Drone (helicopter): a pod on four ducted fans with a sensor eye and
// underslung guns. The Dreadnought (super helicopter) is a winged gunship
// with six engines, a heavier gun battery and gold trim.
export class Airplane {
  constructor(scene, unit, color, isSuper = false) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isSuper = isSuper;
    this.mesh = null;
    this.geometries = [];
    this.rotors = [];
    this.lastPosition = null;
    this.targetRotation = 0;
    this.currentBank = 0;
    this.currentPitch = 0;
    this.phase = Math.random() * Math.PI * 2; // So swarms don't bob in unison
    this.lastAnimTime = null;
    this.lastMeshPosition = new THREE.Vector3();
    this.healthBar = null;
    this.maxHealth = isSuper ? 90 : 30; // SuperHelicopterHealth vs AirplaneHealth
    this.create();
  }

  create() {
    // Scale factor for super helicopters (1.3x larger)
    const scale = this.isSuper ? 1.3 : 1.0;

    // Create parent group for positioning and yaw rotation
    this.mesh = new THREE.Group();

    // Create a yaw group (rotates around Y axis for heading)
    this.yawGroup = new THREE.Group();
    this.mesh.add(this.yawGroup);

    // Create a pitch/bank group for tilting; the craft faces +Z
    this.bankGroup = new THREE.Group();
    this.bankGroup.scale.setScalar(scale);
    this.yawGroup.add(this.bankGroup);

    const body = this.isSuper ? this.buildDreadnought() : this.buildDrone();
    this.bodyMesh = new THREE.Mesh(this.track(bake(body.hull)), armor());
    this.bodyMesh.castShadow = true;
    this.bodyMesh.receiveShadow = true;
    this.bankGroup.add(this.bodyMesh);
    this.bankGroup.add(new THREE.Mesh(this.track(bake(body.glow)), glow()));

    // Fans spin inside their ducts
    const rotorGeometry = this.track(bake(this.buildRotor(body.fanRadius)));
    for (const [x, y, z] of body.fans) {
      const rotor = new THREE.Mesh(rotorGeometry, armor());
      rotor.position.set(x, y, z);
      rotor.rotation.y = Math.random() * Math.PI;
      this.bankGroup.add(rotor);
      this.rotors.push(rotor);
    }

    // Create health bar above the craft (on parent group so it stays upright)
    this.healthBar = new HealthBar(this.maxHealth, 3 * scale, 0.35);
    this.healthBar.getGroup().position.y = 3 * scale;
    this.mesh.add(this.healthBar.getGroup());

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.lastMeshPosition.copy(this.mesh.position);

    this.scene.add(this.mesh);
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  // A ducted fan housing: an octagonal ring with a glowing inner lip
  duct(hull, glowParts, x, y, z, radius, trim) {
    hull.push(colored(new THREE.TorusGeometry(radius, 0.12, 4, 8), SCIFI.white, { x, y, z, rx: Math.PI / 2, ry: Math.PI / 8 }));
    hull.push(colored(new THREE.CylinderGeometry(radius + 0.08, radius + 0.08, 0.1, 8, 1, true), SCIFI.alloyDark, { x, y: y - 0.12, z, ry: Math.PI / 8 }));
    glowParts.push(colored(new THREE.TorusGeometry(radius - 0.1, 0.03, 3, 8), trim, { x, y: y + 0.06, z, rx: Math.PI / 2, ry: Math.PI / 8 }));
    hull.push(colored(new THREE.BoxGeometry(radius * 2, 0.05, 0.08), SCIFI.gunmetal, { x, y: y - 0.06, z }));
    hull.push(colored(new THREE.CylinderGeometry(0.1, 0.12, 0.2, 6), SCIFI.gunmetal, { x, y: y - 0.06, z }));
  }

  buildDrone() {
    const team = this.color;
    const trim = glowColor(team);
    const hull = [
      // Pod: gunmetal belly under a team-coloured shell
      colored(new THREE.SphereGeometry(0.72, 8, 6), SCIFI.gunmetal, { scale: [0.95, 0.6, 1.35] }),
      colored(new THREE.SphereGeometry(0.74, 8, 3, 0, Math.PI * 2, 0, Math.PI / 2), team, { y: 0.03, scale: [0.97, 0.75, 1.38] }),
      colored(new THREE.BoxGeometry(0.2, 0.08, 1.5), SCIFI.white, { y: 0.56, z: -0.05 }),
      // Sensor eye housing on the nose
      colored(new THREE.SphereGeometry(0.28, 8, 5), SCIFI.black, { y: -0.05, z: 0.88 }),
      // Tail fin and antenna
      colored(new THREE.BoxGeometry(0.08, 0.5, 0.5), team, { y: 0.45, z: -0.95, rx: -0.5 }),
      colored(new THREE.CylinderGeometry(0.02, 0.02, 0.6, 4), SCIFI.alloy, { x: 0.25, y: 0.75, z: -0.5 }),
      // Chin gun pod with twin barrels
      colored(new THREE.BoxGeometry(0.5, 0.25, 0.6), SCIFI.alloyDark, { y: -0.5, z: 0.35 }),
      colored(new THREE.CylinderGeometry(0.05, 0.05, 0.8, 5), SCIFI.gunmetal, { x: -0.14, y: -0.56, z: 0.95, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.05, 0.05, 0.8, 5), SCIFI.gunmetal, { x: 0.14, y: -0.56, z: 0.95, rx: Math.PI / 2 })
    ];
    const glowParts = [
      colored(new THREE.SphereGeometry(0.16, 6, 4), trim, { y: -0.05, z: 1.06 }),
      colored(new THREE.SphereGeometry(0.07, 4, 3), 0xffffff, { x: 0.04, y: -0.02, z: 1.2 }),
      colored(new THREE.BoxGeometry(0.06, 0.06, 0.06), trim, { x: 0.25, y: 1.06, z: -0.5 }),
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.06), trim, { x: -0.14, y: -0.56, z: 1.36 }),
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.06), trim, { x: 0.14, y: -0.56, z: 1.36 }),
      // Band of light around the pod's waist
      colored(new THREE.CylinderGeometry(0.7, 0.7, 0.06, 8, 1, true), trim, { y: 0.02, scale: [0.97, 1, 1.38] })
    ];

    // Four arms out to the ducted fans
    const fans = [];
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const x = sx * 1.4;
        const z = sz * 1.15;
        hull.push(beam([sx * 0.4, 0, sz * 0.4], [x, 0.1, z], 0.16, SCIFI.alloy));
        this.duct(hull, glowParts, x, 0.12, z, 0.55, trim);
        fans.push([x, 0.06, z]);
      }
    }
    return { hull, glow: glowParts, fans, fanRadius: 0.5 };
  }

  buildDreadnought() {
    const team = this.color;
    const trim = glowColor(team);
    const hull = [
      // Long armoured fuselage: gunmetal keel, team upper hull, white spine
      colored(prism([[-1.9, -0.4], [1.6, -0.4], [2.2, 0.0], [1.5, 0.3], [-1.7, 0.25]], 1.3), SCIFI.gunmetal),
      colored(prism([[-1.8, 0.25], [1.6, 0.3], [1.0, 0.75], [-1.4, 0.7]], 1.15), team),
      colored(new THREE.BoxGeometry(0.28, 0.1, 2.2), SCIFI.white, { y: 0.76, z: -0.25 }),
      colored(new THREE.BoxGeometry(0.32, 0.04, 2.22), SCIFI.gold, { y: 0.7, z: -0.25 }),
      // Cockpit canopy frame
      colored(new THREE.BoxGeometry(0.8, 0.2, 0.5), SCIFI.black, { y: 0.55, z: 1.2, rx: 0.5 }),
      // Twin tail fins
      colored(new THREE.BoxGeometry(0.08, 0.7, 0.7), team, { x: -0.45, y: 0.75, z: -1.55, rx: -0.5, rz: 0.2 }),
      colored(new THREE.BoxGeometry(0.08, 0.7, 0.7), team, { x: 0.45, y: 0.75, z: -1.55, rx: -0.5, rz: -0.2 }),
      // Chin cannon battery
      colored(new THREE.BoxGeometry(0.7, 0.3, 0.8), SCIFI.alloyDark, { y: -0.55, z: 1.1 })
    ];
    const glowParts = [
      colored(new THREE.BoxGeometry(0.6, 0.06, 0.3), SCIFI.cyan, { y: 0.62, z: 1.28, rx: 0.5 }),
      colored(new THREE.SphereGeometry(0.15, 6, 4), trim, { y: -0.12, z: 2.12 }),
      // Rear thrusters
      colored(new THREE.CylinderGeometry(0.2, 0.2, 0.05, 6), trim, { x: -0.35, y: 0.0, z: -1.97, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.2, 0.2, 0.05, 6), trim, { x: 0.35, y: 0.0, z: -1.97, rx: Math.PI / 2 })
    ];
    for (const sx of [-1, 1]) {
      hull.push(colored(new THREE.CylinderGeometry(0.24, 0.28, 0.4, 6), SCIFI.alloyDark, { x: sx * 0.35, y: 0, z: -1.78, rx: Math.PI / 2 }));
      // Chin barrels and side glow strip
      hull.push(colored(new THREE.CylinderGeometry(0.06, 0.06, 1.0, 5), SCIFI.gunmetal, { x: sx * 0.2, y: -0.6, z: 1.9, rx: Math.PI / 2 }));
      glowParts.push(colored(new THREE.BoxGeometry(0.1, 0.1, 0.06), trim, { x: sx * 0.2, y: -0.6, z: 2.42 }));
      glowParts.push(colored(new THREE.BoxGeometry(0.04, 0.07, 2.8), trim, { x: sx * 0.66, y: 0.05, z: 0.1 }));
    }

    // Swept wings with gold leading edges, team top plates and wing cannons
    const fans = [];
    for (const sx of [-1, 1]) {
      const wing = (w, h, d, color, x, y, z) => colored(new THREE.BoxGeometry(w, h, d), color, { x: sx * x, y, z, ry: -sx * 0.25 });
      hull.push(wing(2.6, 0.14, 1.1, SCIFI.alloyDark, 1.85, 0.05, -0.15));
      hull.push(wing(2.2, 0.06, 0.8, team, 1.75, 0.15, -0.2));
      hull.push(wing(2.6, 0.08, 0.1, SCIFI.gold, 1.85, 0.08, 0.4));
      hull.push(colored(new THREE.CylinderGeometry(0.07, 0.07, 1.0, 5), SCIFI.gunmetal, { x: sx * 1.2, y: -0.15, z: 0.75, rx: Math.PI / 2 }));
      glowParts.push(colored(new THREE.BoxGeometry(0.1, 0.1, 0.06), trim, { x: sx * 1.2, y: -0.15, z: 1.27 }));
      // A ducted fan mid-wing and one at each tip
      this.duct(hull, glowParts, sx * 1.6, 0.1, 0.0, 0.5, trim);
      this.duct(hull, glowParts, sx * 3.15, 0.1, -0.55, 0.5, trim);
      fans.push([sx * 1.6, 0.04, 0.0], [sx * 3.15, 0.04, -0.55]);
    }
    return { hull, glow: glowParts, fans, fanRadius: 0.45 };
  }

  // Three swept blades around a hub, spinning about Y
  buildRotor(radius) {
    const parts = [colored(new THREE.CylinderGeometry(0.09, 0.09, 0.1, 6), SCIFI.alloy, {})];
    for (let i = 0; i < 3; i++) {
      parts.push(colored(new THREE.BoxGeometry(radius, 0.03, 0.14), SCIFI.gunmetal, { x: radius / 2, rx: 0.3 }).rotateY(i * Math.PI * 2 / 3));
    }
    return parts;
  }

  // Spinning fans, a gentle hover bob and a nose-down tilt into movement
  animate() {
    const now = performance.now() / 1000;
    const dt = this.lastAnimTime === null ? 0 : Math.min(now - this.lastAnimTime, 0.1);
    this.lastAnimTime = now;
    if (dt <= 0) return;

    const pos = this.mesh.position;
    const moved = Math.min(Math.hypot(pos.x - this.lastMeshPosition.x, pos.z - this.lastMeshPosition.z), 1);
    this.lastMeshPosition.copy(pos);
    const speed = Math.min(1, moved / dt / 5);

    const targetPitch = speed * (this.isSuper ? 0.12 : 0.22);
    this.currentPitch += (targetPitch - this.currentPitch) * Math.min(1, dt * 3);
    this.bankGroup.rotation.x = this.currentPitch;

    const t = now + this.phase;
    this.bankGroup.position.y = Math.sin(t * 1.8) * 0.12;
    const spin = dt * (this.isSuper ? 18 : 24);
    this.rotors.forEach((rotor, i) => { rotor.rotation.y += i % 2 ? spin : -spin; });
  }

  updatePosition(position) {
    if (this.mesh && this.yawGroup && this.bankGroup) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          // atan2(dx, dz) gives the angle from +Z, the direction the craft faces
          const newTargetRotation = Math.atan2(dx, dz);

          // Calculate turn rate for banking effect
          let turnRate = newTargetRotation - this.targetRotation;
          while (turnRate > Math.PI) turnRate -= Math.PI * 2;
          while (turnRate < -Math.PI) turnRate += Math.PI * 2;

          // Target bank angle based on turn rate (bank into turns)
          const targetBank = Math.max(-0.5, Math.min(0.5, -turnRate * 2));
          this.currentBank += (targetBank - this.currentBank) * 0.1;

          this.targetRotation = newTargetRotation;
        } else {
          // Reduce bank when not turning
          this.currentBank *= 0.95;
        }
      }

      // Smoothly interpolate yaw rotation (apply to yaw group)
      let rotationDiff = this.targetRotation - this.yawGroup.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.yawGroup.rotation.y += rotationDiff * 0.15;

      // Apply banking to bank group (roll around local Z axis)
      this.bankGroup.rotation.z = this.currentBank;

      this.animate();

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
    // Materials are shared; only geometries are ours
    this.geometries.forEach(geometry => geometry.dispose());
    this.geometries = [];
  }
}
